import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  AlertTriangle,
  GitBranch,
  Inbox,
  Bug,
  Server,
  Bot,
  BookOpen,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Hexagon,
  Shield
} from 'lucide-react';
import { fetchNavBadges } from '@/lib/api';
import { useNexusStore } from '@/stores/nexusStore';

const navItems = [
  // Operations
  { path: '/', label: 'Command Center', icon: LayoutDashboard, shortcut: 'CMD', badgeKey: null, group: 'ops' },
  { path: '/incidents', label: 'Incidents', icon: AlertTriangle, shortcut: 'INC', badgeKey: 'incidents', group: 'ops' },
  { path: '/security', label: 'Security', icon: Shield, shortcut: 'SEC', badgeKey: null, group: 'ops' },
  { path: '/changes', label: 'Changes', icon: GitBranch, shortcut: 'CHG', badgeKey: 'changes', group: 'ops' },
  { path: '/requests', label: 'Requests', icon: Inbox, shortcut: 'REQ', badgeKey: 'requests', group: 'ops' },
  { path: '/problems', label: 'Problems', icon: Bug, shortcut: 'PRB', badgeKey: 'problems', group: 'ops' },
  // Intelligence
  { path: null, label: 'INTELLIGENCE', icon: null, group: 'divider' },
  { path: '/agents', label: 'Agents', icon: Bot, shortcut: 'AGT', badgeKey: 'agents', group: 'intel' },
  { path: '/assets', label: 'Assets & CMDB', icon: Server, shortcut: 'AST', badgeKey: 'assets', group: 'intel' },
  { path: '/knowledge', label: 'Knowledge', icon: BookOpen, shortcut: 'KNW', badgeKey: 'knowledge', group: 'intel' },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, shortcut: 'ANL', badgeKey: 'analytics', group: 'intel' },
  // System
  { path: null, label: 'SYSTEM', icon: null, group: 'divider' },
  { path: '/msp', label: 'MSP Overview', icon: Users, shortcut: 'MSP', badgeKey: null, group: 'system' },
  { path: '/runbooks', label: 'Runbooks', icon: BookOpen, shortcut: 'RBK', badgeKey: null, group: 'system' },
  { path: '/executive', label: 'Executive ROI', icon: BarChart3, shortcut: 'ROI', badgeKey: null, group: 'system' },
  { path: '/settings', label: 'Settings', icon: Settings, shortcut: 'SET', badgeKey: null, group: 'system' },
];

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navBadges = useNexusStore((s) => s.navBadges);
  const setNavBadges = useNexusStore((s) => s.setNavBadges);

  const { data: badgeData } = useQuery({
    queryKey: ['nav-badges'],
    queryFn: fetchNavBadges,
    refetchInterval: 10000,
  });

  // Update store when badge data changes
  useEffect(() => {
    if (badgeData) setNavBadges(badgeData);
  }, [badgeData, setNavBadges]);

  return (
    <aside
      data-testid="sidebar"
      className={`flex flex-col h-screen bg-[var(--nexus-bg-surface)] border-r border-[var(--nexus-border)] transition-all duration-200 ${
        collapsed ? 'w-[60px]' : 'w-[220px]'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-3 h-14 border-b border-[var(--nexus-border)]">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[var(--nexus-accent)] bg-opacity-20">
          <Hexagon size={18} className="text-[var(--nexus-accent)]" />
        </div>
        {!collapsed && (
          <div className="flex flex-col">
            <span className="font-heading text-sm font-bold tracking-wide text-[var(--nexus-text-primary)]">NEXUS</span>
            <span className="text-[10px] font-mono text-[var(--nexus-text-muted)] tracking-widest">ITSM v1.0</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto" data-testid="sidebar-nav">
        {navItems.map((item, idx) => {
          // Render section divider
          if (item.group === 'divider') {
            if (collapsed) return <div key={idx} className="mx-3 my-2 h-px bg-[var(--nexus-border)]" />;
            return (
              <div key={idx} className="mx-3 mt-3 mb-1">
                <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--nexus-text-muted)] font-medium">{item.label}</span>
              </div>
            );
          }

          const Icon = item.icon;
          const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
          const badgeCount = item.badgeKey ? navBadges[item.badgeKey] || 0 : 0;

          return (
            <NavLink
              key={item.path || idx}
              to={item.path}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`group flex items-center gap-2.5 mx-2 my-0.5 px-2.5 py-2 rounded-lg text-sm transition-colors duration-150 ${
                isActive
                  ? 'bg-[#0d2847] border border-[var(--nexus-accent)]/30 text-[var(--nexus-accent)]'
                  : 'text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] hover:text-[var(--nexus-text-primary)]'
              }`}
            >
              <Icon size={16} className={isActive ? 'text-[var(--nexus-accent)]' : 'text-[var(--nexus-text-muted)] group-hover:text-[var(--nexus-text-secondary)]'} />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {badgeCount > 0 && (
                    <span className={`font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${
                      isActive
                        ? 'bg-[#0d3d4d] text-[var(--nexus-accent)] border border-[var(--nexus-accent)]/30'
                        : 'bg-[#1a2235] text-[var(--nexus-text-secondary)] border border-[var(--nexus-border)]'
                    }`}>
                      {badgeCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        data-testid="sidebar-collapse-toggle"
        className="flex items-center justify-center h-10 border-t border-[var(--nexus-border)] text-[var(--nexus-text-muted)] hover:text-[var(--nexus-text-secondary)] transition-colors duration-150"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </aside>
  );
};
